import { UpdateTagDocument } from "@/generated/graphql";
import { withToast } from "@/lib/utils/withToast";
import { useMutation } from "@apollo/client/react";

interface TagUpdates {
  name?: string;
  color?: string;
  isArchived?: boolean;
}

/**
 * Hook for editing an existing tag.
 *
 * Only the fields passed are written; the tag's slug id never changes, so a rename leaves every
 * slide already carrying the tag untouched. Apollo's normalized cache picks up the new values
 * from the returned tag, so no cache surgery is needed unless `isArchived` flips — that removes
 * the tag from the active list, which the caller refetches.
 *
 * @returns An object containing:
 *   - updateTag: Async function taking a tag id and the fields to change, returns the tag.
 *   - loading: Boolean indicating if the mutation is in progress
 */
export function useUpdateTag() {
  const [updateTagMutation, { loading }] = useMutation(UpdateTagDocument, {
    refetchQueries: ["Tags"],
  });

  const updateTag = async (id: string, updates: TagUpdates) => {
    return withToast(
      async () => {
        const result = await updateTagMutation({ variables: { id, ...updates } });
        return result.data?.updateTag;
      },
      {
        loading: "Updating tag...",
        success: "Tag updated",
        // Surfaces the server's reason, which is how the editor learns the name is taken.
        error: (error) =>
          error instanceof Error ? error.message : "Failed to update tag. Please try again.",
      },
      "updateTag"
    );
  };

  return {
    updateTag,
    loading,
  };
}
